import React, { useRef, useState, useEffect, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { createPageUrl } from '../utils';
import { ArrowRight, Video, Clapperboard, Play, Zap, Film, Camera, Radio, MonitorPlay, Scissors, Users, Package, Tv, Globe, GraduationCap, Mic, Lightbulb, MapPin } from 'lucide-react';
import { motion } from 'framer-motion';
import Seo from '@/components/Seo';
import SeoSchema from '@/components/SeoSchema';

// Lazy-load heavy below-the-fold and search components to code-split them
// out of the main bundle, reducing unused JS on initial home-page load.
const ReviewsSlider = lazy(() => import('@/components/reviews/ReviewsSlider'));
const HomeSearch = lazy(() => import('@/components/home/HomeSearch'));


export default function Home() {
  const videoRef = useRef(null);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const charityVideoRef = useRef(null);
  const [charityVideoPlaying, setCharityVideoPlaying] = useState(false);
  const cameraVideoRef = useRef(null);

  useEffect(() => {
    const el = cameraVideoRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        el.play().catch(() => {});
      } else {
        el.pause();
      }
    }, { threshold: 0.3 });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handlePlay = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play().catch(() => {});
        setVideoPlaying(true);
      } else {
        videoRef.current.pause();
        setVideoPlaying(false);
      }
    }
  };

  const handleCharityPlay = () => {
    if (charityVideoRef.current) {
      if (charityVideoRef.current.paused) {
        charityVideoRef.current.play().catch(() => {});
        setCharityVideoPlaying(true);
      } else {
        charityVideoRef.current.pause();
        setCharityVideoPlaying(false);
      }
    }
  };
  return (
    <>
      <Seo
        title="Videoed | Video Camera Hire and Rental Video Production"
        description="Videoed — the UK's No.1 independently ranked video camera hire and rental company. Professional camcorders, drones, Apple Macs, and production crew with nationwide delivery."
        canonical="https://videoed.co.uk/"
      />
      
      {/* LocalBusiness + Organization Schema */}
      <SeoSchema schema={{
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": "Videoed",
        "url": "https://videoed.co.uk",
        "logo": "https://media.base44.com/images/public/694bbff8dc754a40cf01606a/c3383b0ce_videoed_logo.png",
        "description": "UK's leading video camera hire and production services supplier. Over 6,800 five-star reviews. Canon, Sony, Blackmagic cameras, teleprompters, drones, and professional crew nationwide.",
        "telephone": "+44-208-050-2345",
        "email": "info@videoed.co.uk",
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "GB",
          "addressRegion": "England"
        },
        "areaServed": {
          "@type": "Country",
          "name": "United Kingdom"
        },
        "sameAs": [
          "https://www.linkedin.com/company/videoed",
          "https://www.facebook.com/videoed",
          "https://twitter.com/videoed"
        ],
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "5.0",
          "reviewCount": "6800"
        }
      }} />
      
      {/* Service Schema for main offerings */}
      <SeoSchema schema={{
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Service",
            "name": "Video Camera Hire",
            "provider": { "@type": "LocalBusiness", "name": "Videoed" },
            "areaServed": { "@type": "Country", "name": "United Kingdom" },
            "description": "Professional video camera hire from Canon, Sony, Blackmagic and more. Daily, weekly and monthly rates.",
            "offers": {
              "@type": "Offer",
              "priceCurrency": "GBP",
              "price": "70.00",
              "availability": "https://schema.org/InStock"
            }
          },
          {
            "@type": "Service",
            "name": "Video Production Services",
            "provider": { "@type": "LocalBusiness", "name": "Videoed" },
            "areaServed": { "@type": "Country", "name": "United Kingdom" },
            "description": "Full video production crew including camera operators, directors, sound recordists and lighting technicians."
          },
          {
            "@type": "Service",
            "name": "Teleprompter Hire",
            "provider": { "@type": "LocalBusiness", "name": "Videoed" },
            "areaServed": { "@type": "Country", "name": "United Kingdom" },
            "description": "Broadcast teleprompter and autocue hire with certified operators.",
            "offers": {
              "@type": "Offer",
              "priceCurrency": "GBP",
              "price": "65.00",
              "availability": "https://schema.org/InStock"
            }
          },
          {
            "@type": "Service",
            "name": "Drone Hire",
            "provider": { "@type": "LocalBusiness", "name": "Videoed" },
            "areaServed": { "@type": "Country", "name": "United Kingdom" },
            "description": "Professional DJI drone hire for aerial filming. 4K and 6K footage.",
            "offers": {
              "@type": "Offer",
              "priceCurrency": "GBP",
              "price": "80.00",
              "availability": "https://schema.org/InStock"
            }
          },
          {
            "@type": "Service",
            "name": "Apple Mac Hire",
            "provider": { "@type": "LocalBusiness", "name": "Videoed" },
            "areaServed": { "@type": "Country", "name": "United Kingdom" },
            "description": "MacBook Pro, Mac Studio and Mac Mini hire with Final Cut Pro and DaVinci Resolve.",
            "offers": {
              "@type": "Offer",
              "priceCurrency": "GBP",
              "price": "60.00",
              "availability": "https://schema.org/InStock"
            }
          }
        ]
      }} />
      <div className="w-full bg-gradient-to-br from-yellow-50 via-orange-50 to-pink-50 relative overflow-hidden">
      {/* Groovy decorative blur elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-20 left-10 w-80 h-80 bg-purple-300/20 rounded-full blur-3xl" />
        <div className="absolute top-60 right-20 w-96 h-96 bg-pink-300/20 rounded-full blur-3xl" />
      </div>
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-orange-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30"></div>
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-pink-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10 py-16 md:py-24">
          <div className="text-center max-w-5xl mx-auto">
            <h1 className="text-6xl md:text-7xl lg:text-8xl font-bold mb-8 leading-tight">
              <span className="bg-gradient-to-r from-purple-300 via-blue-300 to-pink-300 bg-clip-text text-transparent">
                Time to Get
              </span>
              <br />
              <span className="text-gray-900">Videoed</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-10 max-w-3xl mx-auto leading-relaxed">
              Explore the very latest Video Camera Hire and award winning Video Production Services
            </p>

            {/* Search Bar */}
            <Suspense fallback={
              <div className="max-w-2xl mx-auto">
                <div className="w-full rounded-2xl border-2 border-gray-200 bg-white shadow-lg h-[60px] flex items-center pl-12 pr-4 text-gray-400 text-base">
                  Search cameras, teleprompter, crew, guides...
                </div>
              </div>
            }>
              <HomeSearch />
            </Suspense>

            {/* Service Cards */}
            <div className="grid md:grid-cols-2 gap-8 mt-20 max-w-4xl mx-auto">
              <Link 
                to={createPageUrl('video-camera-and-accessory-hire')}
                className="group relative bg-white/90 backdrop-blur-sm rounded-3xl p-10 shadow-xl hover:shadow-2xl transition-all duration-500 overflow-hidden border-2 border-transparent hover:border-orange-200"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-purple-300 via-pink-300 to-orange-200 opacity-0 group-hover:opacity-5 transition-opacity"></div>
                <div className="relative">
                  <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Video className="w-10 h-10 text-purple-600" />
                  </div>
                  <h2 className="text-2xl font-bold mb-3 text-gray-900">Video Camera Hire</h2>
                  <p className="text-gray-600 mb-4">Professional video camera hire and rental</p>
                  <div className="inline-flex items-center gap-2 text-purple-600 font-medium group-hover:gap-4 transition-all">
                    Explore Equipment
                    <ArrowRight className="w-5 h-5" />
                  </div>
                </div>
              </Link>

              <Link 
                to={createPageUrl('video-production-services')}
                className="group relative bg-white/90 backdrop-blur-sm rounded-3xl p-10 shadow-xl hover:shadow-2xl transition-all duration-500 overflow-hidden border-2 border-transparent hover:border-pink-200"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-pink-300 via-purple-300 to-blue-300 opacity-0 group-hover:opacity-5 transition-opacity"></div>
                <div className="relative">
                  <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-pink-100 to-orange-100 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Clapperboard className="w-10 h-10 text-pink-600" />
                  </div>
                  <h2 className="text-2xl font-bold mb-3 text-gray-900">Production Services</h2>
                  <p className="text-gray-600 mb-4">Full-service video production excellence</p>
                  <div className="inline-flex items-center gap-2 text-pink-600 font-medium group-hover:gap-4 transition-all">
                    View Services
                    <ArrowRight className="w-5 h-5" />
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Get Videoed Intro Section */}
      <section className="py-12 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 lg:gap-16 items-center">
              <div className="max-w-lg">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-5 leading-tight">
                  Get Videoed for all Things Video
                </h2>
                <p className="text-base text-gray-500 leading-relaxed">
                  Creating great video content often requires the hire of video camera equipment, accessories or even crew. Well the great news is you can hire just about anything you might need to assist in the creation of your video masterpiece. Not only can you hire the essential Video Camera equipment, accessories, crew and full production facilities, you can even task us to take care of your content creation and trust that your project will be turned around seamlessly.
                </p>
              </div>
              <div className="rounded-2xl overflow-hidden shadow-lg ring-1 ring-gray-100 relative group cursor-pointer" onClick={handlePlay}>
                <video 
                  ref={videoRef}
                  className="w-full h-auto block"
                  poster="https://base44.app/api/apps/694bbff8dc754a40cf01606a/files/mp/public/694bbff8dc754a40cf01606a/ea1b958c5_image.webp" preload="none"
                  onEnded={() => setVideoPlaying(false)}
                  onPause={() => setVideoPlaying(false)}
                >
                  <source src="https://media.base44.com/videos/public/694bbff8dc754a40cf01606a/0884ce701_videoedhomePage-SD480p.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
                {!videoPlaying && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/10 group-hover:bg-black/20 transition-colors">
                    <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:bg-purple-500 transition-all">
                      <Play className="w-7 h-7 text-white ml-1" />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Production Facilities Section */}
      <section className="py-12 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 lg:gap-16 items-center">
              {/* Left Column - Text */}
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-5 leading-tight">
                  Access Full Video Production Facilities to get Your Project Videoed Easily
                </h2>
                <div className="space-y-4 text-base text-gray-500 leading-relaxed">
                  <p>Getting Videoed has never been easier with our full production facilities</p>
                  <p>If you just need one element or a full production then Videoed have you covered for less fluff and more value for money</p>
                  <p>With everything you need under one roof from Video Producers, Video Camera Crew, Video Live Streaming, Teleprompter and Autocue operators, please dive in, nose around and meet the friendliest most customer focused team in the Video Creation Industry</p>
                </div>
                <Link 
                  to={createPageUrl('video-production-services')}
                  className="inline-flex items-center gap-2 mt-8 px-8 py-4 rounded-full text-base font-semibold uppercase tracking-wide bg-gradient-to-r from-purple-300 via-blue-300 to-pink-300 hover:from-purple-400 hover:via-blue-400 hover:to-pink-400 text-white shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                >
                  Get Started with Video Production
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>

              {/* Right Column - Funky Production Cards */}
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: Film, label: "Video Production", color: "from-purple-300 to-pink-300", bg: "bg-purple-100", iconColor: "text-purple-500", link: "/full-production-services" },
                  { icon: Camera, label: "Video Camera Operator", color: "from-blue-300 to-cyan-300", bg: "bg-blue-100", iconColor: "text-blue-500", link: "/camera-video-crew-operators/video-camera-operator" },
                  { icon: Radio, label: "Live Streaming Production", color: "from-green-300 to-emerald-300", bg: "bg-green-100", iconColor: "text-green-500", link: "/video-camera-package-hire/live-streaming-video-hire-package" },
                  { icon: MonitorPlay, label: "Teleprompter Operators", color: "from-orange-300 to-red-300", bg: "bg-orange-100", iconColor: "text-orange-500", link: "/accessory-hire/video-cameras-accessories/teleprompter/teleprompter-operator-with-equipment" },
                  { icon: Scissors, label: "Video Editing", color: "from-indigo-300 to-violet-300", bg: "bg-indigo-100", iconColor: "text-indigo-500", link: "/video-editing-post-production" },
                  { icon: Users, label: "Conference Video Production", color: "from-pink-300 to-rose-300", bg: "bg-pink-100", iconColor: "text-pink-500", link: "/video-camera-package-hire/conference-video-package-hire" },
                ].map((item, i) => {
                  const IconComponent = item.icon;
                  const CardContent = (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      whileHover={{ scale: 1.05, y: -6 }}
                      transition={{ delay: i * 0.05, duration: 0.3 }}
                      className={`group relative ${item.bg} rounded-2xl p-3 shadow-lg hover:shadow-2xl transition-shadow duration-300 border-2 border-transparent hover:border-purple-200 overflow-hidden h-full`}
                    >
                      <div className={`absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br ${item.color} opacity-20 rounded-full blur-xl group-hover:opacity-30 transition-opacity`} />
                      <div className="group-hover:scale-110 transition-transform">
                        <IconComponent className={`w-10 h-10 ${item.iconColor} mb-2 mx-auto`} />
                      </div>
                      <h3 className="text-sm font-bold text-gray-900 text-center mb-1">{item.label}</h3>
                      <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-2 group-hover:translate-x-0">
                        <Zap className="w-4 h-4 text-purple-400" />
                      </div>
                    </motion.div>
                  );
                  return item.link ? (
                    <Link key={i} to={item.link} className="block h-full">{CardContent}</Link>
                  ) : (
                    <div key={i} className="h-full">{CardContent}</div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* We Make it Easy Section */}
      <section className="py-12 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 lg:gap-16 items-center">
              {/* Left - Camera Image */}
              <div className="flex justify-center">
                <video
                  ref={cameraVideoRef}
                  muted
                  loop
                  playsInline
                  preload="none"
                  width="512"
                  height="512"
                  className="w-full max-w-lg object-contain drop-shadow-xl rounded-2xl"
                >
                  <source src="https://media.base44.com/videos/public/694bbff8dc754a40cf01606a/ff8bc3835_videoedvideocamerahireandrental.mp4" type="video/mp4" />
                </video>
              </div>

              {/* Right - Text & CTA */}
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-5 leading-tight">
                  We Make it Easy to Hire Video Kit and get the Content You Need Videoed
                </h2>
                <p className="text-base text-gray-500 leading-relaxed mb-8">
                  The very latest Video Camera Hire Equipment from the leading manufacturers along with the widest range of accessories maintained to perfection to make your hire complete with simply awesome rates and a magical user experience for you
                </p>
                <Link 
                  to={createPageUrl('video-camera-hire')}
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-base font-semibold uppercase tracking-wide bg-gradient-to-r from-purple-300 via-blue-300 to-pink-300 hover:from-purple-400 hover:via-blue-400 hover:to-pink-400 text-white shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                >
                  Get Started with Video Camera Hire
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Charity Section */}
      <section className="py-12 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 lg:gap-16 items-center">
              {/* Left - Text */}
              <div className="max-w-lg">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-5 leading-tight">
                  Charity Support
                </h2>
                <div className="space-y-4">
                  <p className="text-base text-gray-500 leading-relaxed">
                    As a Social Enterprise, we reinvest our revenue into equipment and services that support charities, community projects, and causes that create positive change.
                  </p>
                  <p className="text-base text-gray-500 leading-relaxed">
                    Through our sponsorship scheme, we provide hire and production services at greatly reduced rates or free of charge to organizations making a difference.
                  </p>
                  <p className="text-base text-gray-500 leading-relaxed">
                    If your charity or community project could benefit from our support, we'd love to hear from you.
                  </p>
                </div>
                <div className="mt-8">
                  <Link 
                    to="/charity-support-and-social-enterprise-activity" 
                    className="inline-flex items-center gap-3 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white px-8 py-4 rounded-full text-lg font-medium transition-all transform hover:scale-105 shadow-lg"
                  >
                    Learn About Charity Support
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                </div>
              </div>

              {/* Right - Video */}
              <div className="rounded-2xl overflow-hidden shadow-lg ring-1 ring-gray-100 relative group cursor-pointer" onClick={handleCharityPlay}>
                <video 
                  ref={charityVideoRef}
                  className="w-full h-auto block"
                  poster="https://base44.app/api/apps/694bbff8dc754a40cf01606a/files/mp/public/694bbff8dc754a40cf01606a/48d9cf2b5_image.webp" preload="none"
                  onEnded={() => setCharityVideoPlaying(false)}
                  onPause={() => setCharityVideoPlaying(false)}
                >
                  <source src="https://media.base44.com/videos/public/694bbff8dc754a40cf01606a/6b8ad10cc_charityvideo-esv2-50p-bg-10p-music-10p.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
                {!charityVideoPlaying && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/10 group-hover:bg-black/20 transition-colors">
                    <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:bg-purple-500 transition-all">
                      <Play className="w-7 h-7 text-white ml-1" />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ranked Badge */}
      <section className="py-8 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center gap-4 bg-gradient-to-r from-yellow-50 via-purple-50 to-pink-50 rounded-2xl px-6 py-5 border border-purple-100 shadow-sm">
            <img
              src="https://base44.app/api/apps/694bbff8dc754a40cf01606a/files/mp/public/694bbff8dc754a40cf01606a/7ee4861ab_image.webp"
              alt="Ranked No.1"
              className="h-12 w-auto" width="160" height="48" loading="lazy" decoding="async"
            />
            <div>
              <p className="text-base font-bold text-gray-900">We're Ranked No.1 in Video Camera Hire and Video Production</p>
              <p className="text-sm text-gray-500">with 6,807 5 star reviews on Ranked</p>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Services — AI SEO Section */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-purple-50 border-t border-gray-100">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3 text-center">Popular Services</h2>
            <p className="text-lg text-gray-500 mb-12 text-center max-w-2xl mx-auto">Everything you need to create outstanding video content, all under one roof</p>

            <div className="grid md:grid-cols-2 gap-8">
              {[
                {
                  title: 'Professional Video Camera Hire',
                  icon: Camera,
                  gradient: 'from-purple-400 to-pink-400',
                  badgeBg: 'bg-purple-100',
                  badgeText: 'text-purple-700',
                  content: (
                    <>
                      Our <Link to="/camera-catalogue" className="text-purple-600 hover:underline font-medium">video camera hire</Link> fleet includes the very latest Canon, Sony, and Blackmagic cinema cameras available on daily, weekly, and monthly rates with nationwide delivery. Whether you need a broadcast camcorder for a corporate shoot or a cinema-grade body for a commercial production, every camera leaves our warehouse freshly tested and fully serviced. Explore our <Link to="/video-camera-package-hire" className="text-purple-600 hover:underline font-medium">video camera hire packages</Link> for all-in-one solutions, or browse individual cameras and add <Link to="/video-grip-hire" className="text-purple-600 hover:underline font-medium">grip and accessories</Link> to suit your exact brief.
                    </>
                  ),
                },
                {
                  title: 'Corporate Video Production',
                  icon: Film,
                  gradient: 'from-blue-400 to-cyan-400',
                  badgeBg: 'bg-blue-100',
                  badgeText: 'text-blue-700',
                  content: (
                    <>
                      From boardroom interviews to large-scale brand films, our <Link to="/corporate-films" className="text-purple-600 hover:underline font-medium">corporate video production</Link> team delivers polished, on-brand content on time and on budget. We provide everything from a single <Link to="/camera-video-crew-operators/video-camera-operator" className="text-purple-600 hover:underline font-medium">video camera operator</Link> to a fully crewed production with director, DP, sound, and lighting. Our <Link to="/full-production-services" className="text-purple-600 hover:underline font-medium">full production services</Link> are trusted by leading brands, agencies, and in-house marketing teams across the UK — with transparent pricing and a dedicated production manager on every project.
                    </>
                  ),
                },
                {
                  title: 'Conference Filming',
                  icon: Users,
                  gradient: 'from-green-400 to-emerald-400',
                  badgeBg: 'bg-green-100',
                  badgeText: 'text-green-700',
                  content: (
                    <>
                      Capturing a conference or panel event requires specialist expertise and reliable equipment. Our <Link to="/conferences" className="text-purple-600 hover:underline font-medium">conference filming</Link> service provides experienced multi-camera crews, clean audio feeds from the PA system, and same-day highlights edits. We also offer <Link to="/video-camera-package-hire/conference-video-package-hire" className="text-purple-600 hover:underline font-medium">conference video hire packages</Link> for teams who prefer to self-shoot, with cameras, tripods, and audio ready to go. Need speakers to deliver confidently to camera? Add a <Link to="/accessory-hire/video-cameras-accessories/teleprompter/conference-teleprompter-autocue-operator-with-equipment" className="text-purple-600 hover:underline font-medium">conference teleprompter operator</Link> to keep the message on point.
                    </>
                  ),
                },
                {
                  title: 'Live Streaming Services',
                  icon: Radio,
                  gradient: 'from-orange-400 to-amber-400',
                  badgeBg: 'bg-orange-100',
                  badgeText: 'text-orange-700',
                  content: (
                    <>
                      Reach audiences far beyond the room with our professional <Link to="/live-streaming" className="text-purple-600 hover:underline font-medium">live streaming services</Link>. We broadcast to YouTube, LinkedIn, Vimeo, and custom RTMP destinations with broadcast-quality encoding, redundant 4G/5G backup connectivity, and dedicated stream monitoring throughout your event. Our <Link to="/live-stream-technician" className="text-purple-600 hover:underline font-medium">live stream technicians</Link> handle all the technical setup so your team can focus on the content. For self-streaming setups, our <Link to="/video-camera-package-hire/live-streaming-video-hire-package" className="text-purple-600 hover:underline font-medium">live streaming hire packages</Link> include everything you need to go live.
                    </>
                  ),
                },
                {
                  title: 'Teleprompter and Autocue Operators',
                  icon: MonitorPlay,
                  gradient: 'from-pink-400 to-rose-400',
                  badgeBg: 'bg-pink-100',
                  badgeText: 'text-pink-700',
                  content: (
                    <>
                      A great script deserves a confident delivery. Our certified <Link to="/accessory-hire/video-cameras-accessories/teleprompter/teleprompter-operator-with-equipment" className="text-purple-600 hover:underline font-medium">teleprompter operators</Link> bring broadcast-grade autocue equipment and years of experience working with presenters, executives, and on-camera talent across the UK. Whether you need an <Link to="/accessory-hire/video-cameras-accessories/teleprompter/on-camera-video-production-teleprompter-autocue-operator-with-equipment" className="text-purple-600 hover:underline font-medium">on-camera teleprompter</Link> for a studio shoot or a floor-standing conference autocue for a keynote address, we have the right setup. Read our <Link to="/guides/script-preparation-for-teleprompter" className="text-purple-600 hover:underline font-medium">script preparation guide</Link> to get the most from your session.
                    </>
                  ),
                },
                {
                  title: 'Video Editing & Post Production',
                  icon: Scissors,
                  gradient: 'from-indigo-400 to-purple-400',
                  badgeBg: 'bg-indigo-100',
                  badgeText: 'text-indigo-700',
                  content: (
                    <>
                      Great footage only becomes great content in the edit suite. Our <Link to="/video-editing-post-production" className="text-purple-600 hover:underline font-medium">video editing and post-production</Link> team handles everything from rough assembly cuts to colour grade, motion graphics, and final delivery in any format. We work with footage shot by our own crews as well as rushes captured by your in-house team. Hire one of our <Link to="/apple-hire" className="text-purple-600 hover:underline font-medium">Apple Mac editing suites</Link> if you prefer to keep editing in-house, or let us manage the full workflow end to end — read our <Link to="/guides/post-production-workflow" className="text-purple-600 hover:underline font-medium">post-production workflow guide</Link> for more detail.
                    </>
                  ),
                },
              ].map((service, idx) => {
                const Icon = service.icon;
                return (
                  <motion.div
                    key={service.title}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.05, duration: 0.4 }}
                    whileHover={{ y: -6, transition: { duration: 0.3 } }}
                    className="group relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden"
                  >
                    <div className={`absolute -top-8 -right-8 w-32 h-32 bg-gradient-to-br ${service.gradient} opacity-10 rounded-full blur-2xl group-hover:scale-110 group-hover:opacity-20 transition-all duration-300`} />
                    <div className={`absolute -bottom-6 -left-6 w-20 h-20 bg-gradient-to-tr ${service.gradient} opacity-5 rounded-full blur-xl`} />
                    <div className="relative">
                      <div
                        className={`w-14 h-14 ${service.badgeBg} rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}
                      >
                        <Icon className={`w-7 h-7 ${service.badgeText}`} />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-4">{service.title}</h3>
                      <p className="text-gray-600 text-sm leading-relaxed">{service.content}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Internal Links Hub — SEO pillar links */}
      <section className="py-20 bg-white border-t border-gray-100">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3 text-center">Everything You Need, All in One Place</h2>
            <p className="text-lg text-gray-500 mb-12 text-center max-w-2xl mx-auto">Explore our full range of video camera hire, production services, equipment packages, and professional crew</p>

            {/* Equipment Hire */}
            <div className="mb-10">
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2"><Camera className="w-5 h-5 text-purple-600" /> Equipment Hire</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {[
                  { label: 'Professional Video Camera Hire', path: '/camera-catalogue' },
                  { label: 'Video Camera Hire Packages', path: '/video-camera-package-hire' },
                  { label: 'Conference Video Hire Packages', path: '/video-camera-package-hire/conference-video-package-hire' },
                  { label: 'Live Streaming Equipment Packages', path: '/video-camera-package-hire/live-streaming-video-hire-package' },
                  { label: 'Wedding Video Camera Packages', path: '/video-camera-package-hire/weddings' },
                  { label: 'Professional Drone Hire', path: '/drone-hire' },
                  { label: 'Apple Mac Hire for Video Editing', path: '/apple-hire' },
                  { label: 'Video Grip & Accessory Hire', path: '/video-grip-hire' },
                  { label: 'Teleprompter & Autocue Hire', path: '/accessory-hire/video-cameras-accessories/teleprompter' },
                  { label: 'Camera Tripod & Support Hire', path: '/accessory-hire/video-cameras-accessories/tripod-hire' },
                ].map(({ label, path }) => (
                  <Link key={path} to={path} className="flex items-center gap-2 px-4 py-3 bg-purple-50 hover:bg-purple-100 rounded-xl text-sm font-medium text-purple-800 hover:text-purple-900 transition-colors group">
                    <ArrowRight className="w-3.5 h-3.5 shrink-0 group-hover:translate-x-0.5 transition-transform" />
                    {label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Production Services */}
            <div className="mb-10">
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2"><Film className="w-5 h-5 text-pink-600" /> Production Services</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {[
                  { label: 'End-to-End Video Production Services', path: '/video-production-services' },
                  { label: 'Full Video Production Services', path: '/full-production-services' },
                  { label: 'Corporate Film Production', path: '/corporate-films' },
                  { label: 'Conference Video Production', path: '/conferences' },
                  { label: 'Live Streaming Production Services', path: '/live-streaming' },
                  { label: 'TV Commercial Production', path: '/tv-commercials' },
                  { label: 'Training Video Production', path: '/training-videos' },
                  { label: 'Social Media Video Content', path: '/social-content' },
                  { label: 'Product Demo Video Production', path: '/product-demos' },
                  { label: 'Music Video Production', path: '/music-videos' },
                  { label: 'Video Case Studies', path: '/case-studies' },
                  { label: 'Exhibition Video Production', path: '/exhibitions' },
                  { label: 'Video Editing & Post-Production', path: '/video-editing-post-production' },
                ].map(({ label, path }) => (
                  <Link key={path} to={path} className="flex items-center gap-2 px-4 py-3 bg-pink-50 hover:bg-pink-100 rounded-xl text-sm font-medium text-pink-800 hover:text-pink-900 transition-colors group">
                    <ArrowRight className="w-3.5 h-3.5 shrink-0 group-hover:translate-x-0.5 transition-transform" />
                    {label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Professional Crew */}
            <div className="mb-10">
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2"><Users className="w-5 h-5 text-blue-600" /> Professional Crew</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {[
                  { label: 'Professional Camera Operator Hire', path: '/camera-video-crew-operators/video-camera-operator' },
                  { label: 'Director of Photography Hire', path: '/director-of-photography-hire' },
                  { label: 'Video Director Hire', path: '/video-director-hire' },
                  { label: 'Sound Recordist Hire', path: '/sound-recordist-hire' },
                  { label: 'Gaffer & Lighting Hire', path: '/gaffer-hire' },
                  { label: 'Live Stream Technician Hire', path: '/live-stream-technician' },
                  { label: 'Teleprompter Operator with Equipment', path: '/accessory-hire/video-cameras-accessories/teleprompter/teleprompter-operator-with-equipment' },
                  { label: '1st Assistant Director Hire', path: '/1st-assistant-director-hire' },
                ].map(({ label, path }) => (
                  <Link key={path} to={path} className="flex items-center gap-2 px-4 py-3 bg-blue-50 hover:bg-blue-100 rounded-xl text-sm font-medium text-blue-800 hover:text-blue-900 transition-colors group">
                    <ArrowRight className="w-3.5 h-3.5 shrink-0 group-hover:translate-x-0.5 transition-transform" />
                    {label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Sectors & Special Offers */}
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2"><Globe className="w-5 h-5 text-green-600" /> Who We Work With</h3>
                <div className="grid grid-cols-1 gap-3">
                  {[
                    { label: 'Corporate Video Production Services', path: '/corporate-video-camera-hire-production-services' },
                    { label: 'Video Hire for Agencies & Marketing Teams', path: '/agencies-marketing-video-hire-post-production' },
                    { label: 'Education & Non-Profit Video Hire', path: '/education-non-profit-video-camera-hire' },
                    { label: 'Student & Educational Video Camera Discount', path: '/student-and-educational-discount-video-camera-hire-and-production-services' },
                    { label: 'Video Camera Hire for Individuals & Creators', path: '/individuals-video-camera-hire-production-services' },
                    { label: 'Charity & Social Enterprise Video Support', path: '/charity-support-and-social-enterprise-activity' },
                  ].map(({ label, path }) => (
                    <Link key={path} to={path} className="flex items-center gap-2 px-4 py-3 bg-green-50 hover:bg-green-100 rounded-xl text-sm font-medium text-green-800 hover:text-green-900 transition-colors group">
                      <ArrowRight className="w-3.5 h-3.5 shrink-0 group-hover:translate-x-0.5 transition-transform" />
                      {label}
                    </Link>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2"><Lightbulb className="w-5 h-5 text-orange-600" /> Guides & Resources</h3>
                <div className="grid grid-cols-1 gap-3">
                  {[
                    { label: 'How Much Does Video Production Cost?', path: '/guides/how-much-does-video-production-cost' },
                    { label: 'How to Film a Conference', path: '/guides/how-to-film-a-conference' },
                    { label: 'How to Livestream an Event', path: '/guides/how-to-livestream-an-event' },
                    { label: 'Best Cameras for Corporate Video', path: '/guides/best-cameras-for-corporate-video' },
                    { label: 'Canon C400 vs Sony FX6', path: '/guides/canon-c400-vs-sony-fx6' },
                    { label: 'Client Reviews', path: '/client-reviews' },
                  ].map(({ label, path }) => (
                    <Link key={path} to={path} className="flex items-center gap-2 px-4 py-3 bg-orange-50 hover:bg-orange-100 rounded-xl text-sm font-medium text-orange-800 hover:text-orange-900 transition-colors group">
                      <ArrowRight className="w-3.5 h-3.5 shrink-0 group-hover:translate-x-0.5 transition-transform" />
                      {label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>

    {/* Client Reviews Slider */}
    <Suspense fallback={null}>
      <ReviewsSlider page="home" headingText="Videoed Success Stories" />
    </Suspense>
    </>
    );
    }